import { Component, OnInit } from '@angular/core';
import { BaseComponent, UtilsArray, UtilsMisc, UtilsString } from '@pit/core';
import { IDtoDepot, IDtoSetting, IDtoUser, IDtoUserRole, UserState } from '@pit/frontend-model';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { Observable } from 'rxjs';
import { PinItTrackFrontendServicesAccessor } from '../../module.core/accessor/implementations/pin.it.track.frontend.services.accessor';

@Component({
  selector: 'pin-it-track-frontend-user-device-dialog',
  templateUrl: './pin.it.track.frontend.user.device.dialog.component.html',
  styleUrls: ['./pin.it.track.frontend.user.device.dialog.component.scss']
})
export class PinItTrackFrontendUserDeviceDialogComponent extends BaseComponent {

  private _isActive: boolean = true;
  private _isAdmin: boolean = false;
  private _isAdminSuper: boolean = false;
  private _isDispatcher: boolean = false;
  private _userNamesValidRegex: RegExp | null = null;
  private _userNamesValidExamples: string = '';

  constructor(private servicesAccessor: PinItTrackFrontendServicesAccessor, private dialogRef: DynamicDialogRef, private dialogConfig: DynamicDialogConfig) {
    super(servicesAccessor.coreServicesAccessor.applicationService);

    this.user = this.dialogConfig.data.user;
    this._isActive = (this.user.userState === UserState.Avtive);
    this._isAdmin = this.user.isAdmin;
    this._isAdminSuper = this.user.isAdminSuper;
    this._isDispatcher = this.user.isDispatcher;

    if (this.user.id === null) {
      this.user.passwordResetRequested = true;
    }
  }

  public get canSave(): boolean {
    return !this.isUserNameInvalid && !this.isLastNameInvalid;
  }

  public get isUserNameInvalid(): boolean {
    if (UtilsString.stringIsNullOrEmpty(this.user?.userName)) {
      return true;
    }

    if (this._userNamesValidRegex === null) {
      return true;
    }

    return !this._userNamesValidRegex.test(this.user.userName);
  }

  public get isLastNameInvalid(): boolean {
    return UtilsString.stringIsNullOrEmpty(this.user?.lastName) || (this.user?.lastName?.length < 2);
  }

  public currentUser: IDtoUser = this.servicesAccessor.currentUser;

  public get isActive(): boolean {
    return this._isActive;
  }

  public set isActive(value: boolean) {
    if (value !== this.isActive) {
      this._isActive = value;

      if (this.isActive === true) {
        this.user.userState = UserState.Avtive;
      } else {
        this.user.userState = UserState.Deactive;
      }
    }
  }

  public user: IDtoUser = {};

  public get userNameValidationMessage(): string {
    return `Benutzername muss einem gültigen Format entsprechen. Beispiele: ${this._userNamesValidExamples}`;
  }

  public async cancelAsync(): Promise<void> {
    this.dialogRef.close();
  }

  public async saveAsync(): Promise<void> {
    let success = false;
    try {
      console.log('saving user', this.user);

      this.servicesAccessor.eventService.useMethod('busy.on');
      while (this.user.userName.startsWith('00') === false) {
        this.user.userName = '0' + this.user.userName;
      }
      this.user.userRoles = [...(await this.servicesAccessor.entityStoreFacadesAccessor.userRoleStoreFacade.getUserRolesForDeviceAsync())];
      await this.servicesAccessor.dataServicesAccessor.userDataService.saveAsync(this.user);
      success = true;
    } catch (error: any) {
      // Check if this is a duplicate username error (HTTP 409 Conflict)
      if (error && error.status === 409) {
        // Show specific confirmation dialog for duplicate username
        this.servicesAccessor.eventService.sendEvent('confirmation', {
          message: 'Ein Benutzer mit dieser Benutzerkennung existiert bereits.',
          header: 'Benutzerkennung bereits vorhanden',
          icon: 'pi pi-exclamation-triangle',
          acceptLabel: 'OK',
          rejectLabel: null, // Hide cancel button
          callback: {
            method: async (confirmed: boolean) => {
              // No action needed, just acknowledgment
            },
            data: null
          }
        });
        return; // Keep dialog open, don't close
      } else {
        // For other errors, show error
        this.servicesAccessor.eventService.useMethod('error.show', error);
      }
    } finally {
      this.servicesAccessor.eventService.useMethod('busy.off');
    }

    // Only close dialog on success
    if (success) {
      this.dialogRef.close();
    }
  }

  protected override async doOnAfterViewInitAsync(): Promise<void> {
    await super.doOnAfterViewInitAsync();

    try {
      this.servicesAccessor.eventService.useMethod('busy.on');
      await this.servicesAccessor.entityStoreFacadesAccessor.userRoleStoreFacade.loadItemsAsync();
      await this.loadUserNamesValidSettingAsync();
    } catch (error) {
      this.servicesAccessor.eventService.useMethod('error.log', error);
    } finally {
      this.servicesAccessor.eventService.useMethod('busy.off');
    }
  }

  private async loadUserNamesValidSettingAsync(): Promise<void> {
    try {
      const mandatorId = this.currentUser?.mandator?.id;
      if (mandatorId) {
        const settings = await this.servicesAccessor.dataServicesAccessor.settingDataService.getSettingsForMandatorAsync(mandatorId);
        const userNamesValidSetting = settings.find(s => s.paramKey === 'userNamesValid');

        if (userNamesValidSetting && userNamesValidSetting.paramValue) {
          this._userNamesValidRegex = new RegExp(userNamesValidSetting.paramValue);
          this._userNamesValidExamples = this.generateExamplesFromRegex(userNamesValidSetting.paramValue);
        }
      }
    } catch (error) {
      console.error('Error loading userNamesValid setting:', error);
      // Fallback to default regex if loading fails
      this._userNamesValidRegex = /(^003[0-9]{4}$)|(^091[0-9]{5})|(^0091[0-9]{3})/;
      this._userNamesValidExamples = '0031234, 09112345, 0091123';
    }
  }

  private generateExamplesFromRegex(regexPattern: string): string {
    try {
      // Simple static examples based on common patterns
      if (regexPattern.includes('003[0-9]{4}')) {
        return '0031234, 09112345, 0091123';
      }
      if (regexPattern.includes('[0-9]')) {
        return '0031234, 09112345, 0091123';
      }
      return '0031234, 09112345, 0091123';
    } catch (error) {
      console.error('Error generating examples from regex:', error);
      return '0031234, 09112345, 0091123';
    }
  }


  private userRolesForWeb: IDtoUserRole[] = [];

}
